import { type MaybeRef, computed, toRef, toValue } from "vue";
import { useI18n } from "vue-i18n";

import * as Sentry from "@sentry/vue";
import { useMutation, useQuery } from "@tanstack/vue-query";

import { QUERY_KEYS } from "@/helpers/constants/queryKeys";
import { useNotification } from "@/hooks/notifications";
import { queryClient } from "@/query/queryClient";
import applicationsApiService from "@/services/modules/applications";
import metadataApiService from "@/services/modules/metadata";

import type {
  ApplicationMetadata,
  ApplicationMetadataCreatePayload,
  ApplicationMetadataSyncPayload,
} from "@/models/applications";
import type { Metadata, MetadataInput } from "@/schemas/metadata";

// ---------------------------------------------------------------------------
// Application-specific hooks (used by MetadataSection, MetadataAddModal)
// Will be replaced by FLO-2096 — do not extend.
// ---------------------------------------------------------------------------

interface UseMetadataQueryOptions {
  enabled?: MaybeRef<boolean>;
}

export const useMetadataQuery = (
  applicationId: MaybeRef<string | undefined>,
  options: UseMetadataQueryOptions = {}
) => {
  const { enabled = true } = options;
  const applicationIdRef = toRef(applicationId);

  return useQuery({
    queryKey: computed(() =>
      QUERY_KEYS.METADATA.byApplication(applicationIdRef.value ?? "")
    ),
    queryFn: async (): Promise<ApplicationMetadata[]> => {
      if (!applicationIdRef.value) {
        return [];
      }
      return applicationsApiService.getMetadata(applicationIdRef.value);
    },
    enabled: computed(() => {
      const enabledValue = toRef(enabled).value;
      return enabledValue && !!applicationIdRef.value;
    }),
  });
};

export const useCreateMetadataMutation = (
  applicationId: MaybeRef<string | undefined>
) => {
  const { t } = useI18n();
  const applicationIdRef = toRef(applicationId);

  return useMutation({
    mutationFn: async (
      payload: ApplicationMetadataCreatePayload
    ): Promise<ApplicationMetadata> => {
      if (!applicationIdRef.value) {
        throw new Error(t("COMMON.ERROR_OCCURRED"));
      }
      return applicationsApiService.createMetadata(
        applicationIdRef.value,
        payload
      );
    },
    onSuccess: (newMetadata) => {
      queryClient.setQueryData<ApplicationMetadata[]>(
        QUERY_KEYS.METADATA.byApplication(applicationIdRef.value ?? ""),
        (oldData) => (oldData ? [...oldData, newMetadata] : [newMetadata])
      );
    },
  });
};

export const useDeleteMetadataMutation = (
  applicationId: MaybeRef<string | undefined>
) => {
  const { t } = useI18n();
  const applicationIdRef = toRef(applicationId);

  return useMutation({
    mutationFn: async (metadataId: number): Promise<void> => {
      if (!applicationIdRef.value) {
        throw new Error(t("COMMON.ERROR_OCCURRED"));
      }
      await applicationsApiService.deleteMetadata(
        applicationIdRef.value,
        metadataId
      );
    },
    onMutate: async (deletedId) => {
      const queryKey = QUERY_KEYS.METADATA.byApplication(
        applicationIdRef.value ?? ""
      );
      await queryClient.cancelQueries({ queryKey });

      const previousData =
        queryClient.getQueryData<ApplicationMetadata[]>(queryKey);

      queryClient.setQueryData<ApplicationMetadata[]>(
        queryKey,
        (oldData) => oldData?.filter((item) => item.id !== deletedId) ?? []
      );

      return { previousData };
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          QUERY_KEYS.METADATA.byApplication(applicationIdRef.value ?? ""),
          context.previousData
        );
      }
    },
  });
};

export const useSyncMetadataMutation = (
  applicationId: MaybeRef<string | undefined>
) => {
  const { t } = useI18n();
  const { showMessage } = useNotification();
  const applicationIdRef = toRef(applicationId);

  return useMutation({
    mutationFn: async (
      payload: ApplicationMetadataSyncPayload
    ): Promise<void> => {
      if (!applicationIdRef.value) {
        throw new Error(t("COMMON.ERROR_OCCURRED"));
      }
      await applicationsApiService.syncMetadataWithFunders(
        applicationIdRef.value,
        payload
      );
    },
    onSuccess: () => {
      showMessage(t("DEALS.TRACKING.SYNCED_SUCCESSFULLY"), "success");
    },
  });
};

// ---------------------------------------------------------------------------
// Generic metadata hooks (FLO-2093 — aligned with FLO-2096 patterns)
// ---------------------------------------------------------------------------

interface MetadataHookOptions {
  modelType: MaybeRef<string>;
  modelId: MaybeRef<number | undefined>;
}

export const useGenericMetadataQuery = ({
  modelType,
  modelId,
  enabled = true,
}: MetadataHookOptions & {
  enabled?: MaybeRef<boolean>;
}) => {
  const modelTypeRef = toRef(modelType);
  const modelIdRef = toRef(modelId);

  return useQuery({
    queryKey: ["metadata", modelTypeRef, modelIdRef] as const,
    queryFn: async (): Promise<Metadata[]> => {
      return metadataApiService.list({
        model_type: toValue(modelType),
        model_id: modelIdRef.value!,
      });
    },
    enabled: computed(() => toValue(enabled) && !!toValue(modelId)),
  });
};

export const useSaveGenericMetadataMutation = ({
  modelType,
  modelId,
}: MetadataHookOptions) => {
  return useMutation({
    mutationFn: async (
      params:
        | { mode: "create"; payload: MetadataInput }
        | { mode: "update"; metadataId: number; payload: MetadataInput }
    ): Promise<Metadata> => {
      if (params.mode === "update") {
        return metadataApiService.update({
          metadataId: params.metadataId,
          payload: params.payload,
        });
      }
      const id = toValue(modelId);
      if (!id) {
        const error = new Error("Create metadata called without model ID");
        Sentry.captureException(error);
        return Promise.reject(error);
      }
      return metadataApiService.create({
        model_type: toValue(modelType),
        model_id: id,
        ...params.payload,
      });
    },
    onSuccess: () => {
      const id = toValue(modelId);
      if (!id) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ["metadata", toValue(modelType), id],
      });
    },
  });
};

export const useDeleteGenericMetadataMutation = ({
  modelType,
  modelId,
}: MetadataHookOptions) => {
  return useMutation({
    mutationFn: async (metadataId: number): Promise<void> => {
      await metadataApiService.delete(metadataId);
    },
    onSuccess: () => {
      const id = toValue(modelId);
      if (!id) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ["metadata", toValue(modelType), id],
      });
    },
  });
};
